/**
 * AI-Generated Roadmap Response Panel
 *
 * Rich display for build_roadmap results showing phase breakdown,
 * item lists with points/category, totals summary, and "Accept All" button.
 *
 * Used in two places:
 *   1. Roadmap Builder page 2 — when AI Strategy Assistant builds a roadmap
 *   2. Agent Chat panel — when user asks "build me a roadmap" via chat
 */
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import SendIcon from '@mui/icons-material/Send'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import React, { useState } from 'react'
import { SERVICE_LINE_COLORS } from './icons'
import { normalizeServiceLine, SERVICE_LINE_LABELS } from './types'

// ─── Types matching backend buildRoadmap response ────────────────────────────

export interface RoadmapPhaseItem {
  title: string
  sku: string
  service_line: string
  points: number
  price: number
  category: 'strategic' | 'executable'
  delivery_role: string
  ai_enhanced: boolean
}

export interface RoadmapPhase {
  phase: string
  budget: number
  usedPoints: number
  utilization: number
  itemCount: number
  strategicCount: number
  executableCount: number
  items: RoadmapPhaseItem[]
}

export interface GeneratedRoadmap {
  config: {
    duration_months: number
    include_setup: boolean
    monthly_budget_points: number
    setup_budget_points: number
    rate_per_point: number
    cs_reserve_pct: number
    service_lines: string[] | string
  }
  phases: RoadmapPhase[]
  totals: {
    total_points: number
    total_investment: number
    total_items: number
  }
  narrative?: string
}

// ─── Component ───────────────────────────────────────────────────────────────

interface AIRoadmapResponseProps {
  roadmap: GeneratedRoadmap
  onAcceptAll?: () => void
  onAcceptPhase?: (phase: string) => void
  onRevise?: (instructions: string) => void
  onDismiss?: () => void
  onBackToSetup?: () => void
  isAccepted?: boolean
  isRevising?: boolean
  compact?: boolean // for agent chat panel (narrower layout)
}

const AIRoadmapResponse: React.FC<AIRoadmapResponseProps> = ({
  roadmap,
  onAcceptAll,
  onAcceptPhase,
  onRevise,
  onDismiss,
  onBackToSetup,
  isAccepted = false,
  isRevising = false,
  compact = false,
}) => {
  const [expandedPhase, setExpandedPhase] = useState<string | false>(roadmap.phases[0]?.phase || false)
  const [showRevise, setShowRevise] = useState(false)
  const [reviseText, setReviseText] = useState('')
  const theme = useTheme()

  const { totals, config, phases } = roadmap
  const rate = config.rate_per_point

  // AI accent colors from theme
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'
  const aiSecondary = theme.palette.text.ai_accent_secondary ?? '#5B8DEF'
  const aiBg = theme.palette.background.ai_accent_bg ?? alpha(aiAccent, 0.03)
  const aiBorder = theme.palette.background.ai_accent_border ?? alpha(aiAccent, 0.3)
  const aiGradient = theme.palette.background.ai_accent_gradient ?? `linear-gradient(135deg, ${aiAccent}, ${aiSecondary})`
  const aiGradientHover = theme.palette.background.ai_accent_gradient_hover ?? `linear-gradient(135deg, ${aiAccent}, ${aiSecondary})`

  const phaseLabel = (phase: string) => {
    if (phase === 'setup') return 'Setup'
    // Consolidated range: "months-4-12" → "Months 4–12"
    const rangeMatch = phase.match(/^months-(\d+)-(\d+)$/)
    if (rangeMatch) return `Months ${rangeMatch[1]}\u2013${rangeMatch[2]}`
    return `Month ${phase.replace('month-', '')}`
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: aiBorder,
        bgcolor: aiBg,
        overflow: 'hidden',
      }}
    >
      {/* ─── Header banner ─────────────────────────────────────────── */}
      <Box
        sx={{
          px: compact ? 1.5 : 2.5,
          py: compact ? 1.25 : 1.75,
          background: `linear-gradient(135deg, ${alpha(aiAccent, 0.12)}, ${alpha(aiSecondary, 0.08)})`,
          borderBottom: '1px solid',
          borderColor: alpha(aiAccent, 0.15),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20, color: aiAccent }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: compact ? 13 : 14 }}>
            AI-Generated Roadmap
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {config.duration_months}-month engagement &middot; ${rate}/pt
          </Typography>
        </Box>
        {isAccepted && (
          <Chip
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 14 }} />}
            label="Accepted"
            size="small"
            sx={{
              height: 24,
              fontSize: 11,
              fontWeight: 600,
              bgcolor: alpha(theme.palette.text.complete, 0.1),
              color: theme.palette.text.complete,
              borderColor: alpha(theme.palette.text.complete, 0.3),
              border: '1px solid',
            }}
          />
        )}
        {/* Dismiss button */}
        {onDismiss && !isAccepted && (
          <Tooltip title="Dismiss roadmap">
            <IconButton
              size="small"
              onClick={onDismiss}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: theme.palette.text.error, bgcolor: alpha(theme.palette.text.error, 0.08) },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ─── Totals summary row ────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          gap: compact ? 1.5 : 3,
          px: compact ? 1.5 : 2.5,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexWrap: 'wrap',
        }}
      >
        <StatBox label="Total Points" value={totals.total_points.toLocaleString()} />
        <StatBox label="Investment" value={`$${totals.total_investment.toLocaleString()}`} highlight />
        <StatBox label="Services" value={String(totals.total_items)} />
        <StatBox label="Phases" value={String(phases.length)} />
      </Box>

      {/* ─── Narrative (if provided) ───────────────────────────────── */}
      {roadmap.narrative && (
        <Box sx={{ px: compact ? 1.5 : 2.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.5, fontStyle: 'italic' }}>
            {roadmap.narrative}
          </Typography>
        </Box>
      )}

      {/* ─── Phase accordions ──────────────────────────────────────── */}
      <Box sx={{ px: compact ? 0.5 : 1, py: 0.5 }}>
        {phases.map(phase => (
          <Accordion
            key={phase.phase}
            expanded={expandedPhase === phase.phase}
            onChange={(_, expanded) => setExpandedPhase(expanded ? phase.phase : false)}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: 'transparent',
              '&:before': { display: 'none' },
              '& .MuiAccordionSummary-root': {
                minHeight: 40,
                px: 1.5,
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mr: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13 }}>
                  {phaseLabel(phase.phase)}
                </Typography>
                <Chip
                  label={`${phase.itemCount} items`}
                  size="small"
                  sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                />
                <Chip
                  label={`${phase.usedPoints} pts`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: alpha(aiSecondary, 0.1),
                    color: aiSecondary,
                  }}
                />
                <Box sx={{ flex: 1 }} />
                <Box sx={{ width: 60, mr: 0.5 }}>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(phase.utilization, 100)}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.text.primary, 0.06),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: phase.utilization > 100 ? theme.palette.text.error : phase.utilization > 80 ? theme.palette.text.warning : theme.palette.text.complete,
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10, minWidth: 28 }}>
                  {phase.utilization}%
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
              {/* Strategic / executable counts */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                {phase.strategicCount > 0 && (
                  <Chip
                    label={`${phase.strategicCount} Strategic`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: alpha(theme.palette.text.link, 0.1),
                      color: theme.palette.text.link,
                    }}
                  />
                )}
                {phase.executableCount > 0 && (
                  <Chip
                    label={`${phase.executableCount} Executable`}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: alpha(theme.palette.text.highlight, 0.1),
                      color: theme.palette.text.highlight,
                    }}
                  />
                )}
              </Box>

              {/* Items list */}
              {phase.items.map((item, idx) => {
                const slCode = normalizeServiceLine(item.service_line || '')
                const slColor = SERVICE_LINE_COLORS[slCode] || '#888'
                const slLabel = SERVICE_LINE_LABELS[slCode] || item.service_line || ''
                return (
                  <Box
                    key={`${item.sku}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      py: 0.5,
                      px: 0.5,
                      borderRadius: 1,
                      '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.03) },
                    }}
                  >
                    {/* Category dot */}
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: item.category === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
                        flexShrink: 0,
                      }}
                    />
                    {/* Title */}
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontSize: 12,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.title}
                    </Typography>
                    {/* Service line chip */}
                    {!compact && (
                      <Chip
                        label={slLabel}
                        size="small"
                        sx={{
                          height: 16,
                          fontSize: 9,
                          bgcolor: `${slColor}15`,
                          color: slColor,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    )}
                    {/* AI enhanced badge */}
                    {item.ai_enhanced && <SmartToyIcon sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />}
                    {/* Points */}
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: slColor,
                        fontSize: 11,
                        minWidth: 36,
                        textAlign: 'right',
                      }}
                    >
                      {item.points}pts
                    </Typography>
                  </Box>
                )
              })}

              {/* Per-phase accept button */}
              {onAcceptPhase && !isAccepted && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => onAcceptPhase(phase.phase)}
                  sx={{
                    mt: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    color: aiAccent,
                    textTransform: 'none',
                  }}
                >
                  + Accept this phase
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Divider />

      {/* ─── Actions footer ────────────────────────────────────────── */}
      {!isAccepted && (
        <Box sx={{ px: compact ? 1.5 : 2.5, py: 1.5 }}>
          {/* Accept All */}
          {onAcceptAll && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<RocketLaunchIcon sx={{ fontSize: 16 }} />}
                onClick={onAcceptAll}
                disabled={isRevising}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 13,
                  px: 4,
                  py: 1,
                  borderRadius: 2,
                  background: aiGradient,
                  '&:hover': {
                    background: aiGradientHover,
                  },
                }}
              >
                Accept All &mdash; Add {totals.total_items} items to roadmap
              </Button>
            </Box>
          )}

          {/* Revise / Back row */}
          {(onRevise || onBackToSetup) && (
            <>
              <Divider sx={{ mb: 1.5 }} />

              {/* Revision toggle + back button */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: showRevise ? 1 : 0 }}>
                {onRevise && (
                  <Button
                    size="small"
                    variant={showRevise ? 'outlined' : 'text'}
                    startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setShowRevise(!showRevise)}
                    disabled={isRevising}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 600,
                      color: aiAccent,
                      borderColor: showRevise ? alpha(aiAccent, 0.4) : undefined,
                      '&:hover': { borderColor: alpha(aiAccent, 0.6) },
                    }}
                  >
                    Revise
                  </Button>
                )}
                <Box sx={{ flex: 1 }} />
                {onBackToSetup && (
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                    onClick={onBackToSetup}
                    disabled={isRevising}
                    sx={{
                      textTransform: 'none',
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'text.secondary',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    Back to Setup
                  </Button>
                )}
              </Box>

              {/* Revision input */}
              <Collapse in={showRevise}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={4}
                    fullWidth
                    placeholder="e.g. Add more SEO items in months 1-3, reduce paid media budget, swap out the content audit for a technical SEO audit..."
                    value={reviseText}
                    onChange={e => setReviseText(e.target.value)}
                    disabled={isRevising}
                    sx={{
                      '& .MuiInputBase-root': {
                        fontSize: 13,
                        bgcolor: aiBg,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(aiAccent, 0.2),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(aiAccent, 0.4),
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!reviseText.trim() || isRevising}
                    onClick={() => {
                      if (reviseText.trim() && onRevise) {
                        onRevise(reviseText.trim())
                        setReviseText('')
                        setShowRevise(false)
                      }
                    }}
                    sx={{
                      minWidth: 40,
                      height: 40,
                      mt: 0.5,
                      borderRadius: 1.5,
                      background: aiGradient,
                      '&:hover': { background: aiGradientHover },
                    }}
                  >
                    {isRevising ? (
                      <SmartToyIcon
                        sx={{
                          fontSize: 16,
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
                        }}
                      />
                    ) : (
                      <SendIcon sx={{ fontSize: 16 }} />
                    )}
                  </Button>
                </Box>
              </Collapse>
            </>
          )}
        </Box>
      )}
    </Box>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const StatBox: React.FC<{ label: string; value: string; highlight?: boolean }> = ({ label, value, highlight }) => {
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'
  return (
    <Box sx={{ textAlign: 'center', minWidth: 60 }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontSize: 15,
          color: highlight ? aiAccent : 'text.primary',
        }}
      >
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
        {label}
      </Typography>
    </Box>
  )
}

export default AIRoadmapResponse
